import { useEffect, useRef, useState } from "react";
import client from "../api/client";

/** Normalizes API-relative paths like `/admin/...` for axios baseURL joining. */
function normalizeAdminPath(path: string): string {
  return path.startsWith("/") ? path.slice(1) : path;
}

/**
 * Fetches image bytes with the authenticated API client and exposes a blob: URL for <img src>.
 * Revokes the URL when the path changes or on unmount.
 */
export function useBlobImageUrl(relativePath: string | null | undefined): {
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
} {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!relativePath) {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    client
      .get(normalizeAdminPath(relativePath), { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(response.data);
        urlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setBlobUrl(null);
          setError("Could not load image.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [relativePath]);

  return { blobUrl, loading, error };
}
